import {
  FORMAT_DATE_DEFAULT,
  FORMAT_TIME_DEFAULT,
  TimeFunction,
} from "@/common/datetime/TimeFunction";
import { BuildRCAttribute } from "@/common/function/RCAttribute";
import Canvas from "@/components/ui/Canvas";
import { ICanvasProps } from "@/components/ui/Canvas/Models";
import {
  AttendeeIcon,
  RenderIconCalendar,
  RenderIconDismiss,
  RenderIconInfo,
} from "@/components/ui/Icons/IconsCustom";
import ResourceItem from "@/components/ui/ResourceItem";
import TimeLine from "@/components/ui/TimeLine";
import { TimeRangeSliderCanvas } from "@/components/ui/TimeRangeSlider";
import { RootState } from "@/redux";
import { IOrganizationStore } from "@/redux/slices/organizations/OrganizationModel";
import { ISettingStore } from "@/redux/slices/settings/SettingModel";
import {
  AttendeeInfo,
  DefaultAvailabilityViewInterval,
  IAttendeeStatusRequest,
} from "@/services/attendee-service/entity/AttendeeEntity";
import { Avatar, makeStyles } from "@fluentui/react-components";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { IAttendeeProps } from "./Model";
import {
  AttendeeBaseWrapper,
  AttendeeButtonsWrapper,
  AttendeeInfoWrapper,
  HeaderTitleWrapper,
  StickyElementWrapper,
} from "./Style";
import { IResourceStore } from "@/redux/slices/resources/ResourceReducerModel";
// import Availability from "@/view/addin/components/availability";
import { useQuery } from "@tanstack/react-query";
import LoadingCustom from "@/components/ui/Loading";
import AttendeeCalendar from "@/view/addin/components/attendee-calendar";
import ScrollDismissTooltip from "@/components/ui/Tooltip";
import { ITimeLineEvent } from "@/components/ui/TimeLine/Models";
import { cloneDeep } from "lodash";
// import { IRefComponentType } from "@/view/addin/components/attendee-calendar/Models";

const useStyles = makeStyles({
  tooltip: {
    maxWidth: "none",
    whiteSpace: "normal",
  },
});

const Attendee = (props: IAttendeeProps) => {
  // const refCalendar: RefObject<IRefComponentType | null> = useRef(null);
  const orgStore: IOrganizationStore = useSelector(
    (state: RootState) => state.orgInfo
  ) as IOrganizationStore;

  const settingStore: ISettingStore = useSelector(
    (state: RootState) => state.settingInfo
  ) as ISettingStore;
  const dateFormat = settingStore?.dateTimeFormat.dateTimeSeparator;
  const timeFormat = settingStore?.dateTimeFormat.timeFormat;
  const firstDayOfWeek = Number(
    settingStore?.dateTimeFormat.firstDayOfWeek || "0"
  );

  const resourceStore: IResourceStore = useSelector(
    (state: RootState) => state.resources
  ) as IResourceStore;

  const timeZone = orgStore.organizer?.timeZone;

  const attendees: AttendeeInfo[] = (
    resourceStore.attendees.mainAttendee
      ? [resourceStore.attendees.mainAttendee]
      : []
  ).concat(resourceStore?.attendees.listAttendee || []);

  const { t } = useTranslation();
  const [timeSlider, setTimeSlider] = useState(
    orgStore.outlookInfo?.displayStartTime || new Date()
  );
  const [eventNextDate, setEventNextDate] = useState<
    Record<string, { startDate: string[]; event: ITimeLineEvent[] }>
  >({});

  const [attendeeStatusRequest, setAttendeeStatusRequest] =
    useState<IAttendeeStatusRequest | null>(
      attendees.length
        ? {
            startTime: {
              dateTime: orgStore.outlookInfo?.start,
              timeZone: timeZone,
            },
            endTime: {
              dateTime: orgStore.outlookInfo?.end,
              timeZone: timeZone,
            },
            schedules: attendees.map((item) => item.email),
            availabilityViewInterval: DefaultAvailabilityViewInterval,
          }
        : null
    );

  const attendeeStatusQuery = useQuery<AttendeeInfo[]>({
    queryKey: [
      "attendeeStatus",
      attendeeStatusRequest?.startTime.dateTime,
      attendeeStatusRequest?.endTime.dateTime,
      attendeeStatusRequest?.schedules,
    ],
    queryFn: () => onFetchData(attendeeStatusRequest),
    staleTime: 20 * 1000,
    enabled: !!attendeeStatusRequest?.schedules?.length,
  });

  const onFetchData = async (
    request: IAttendeeStatusRequest | null,
    isNextDate?: boolean
  ): Promise<AttendeeInfo[]> => {
    if (props.onGetAttendeeStatus && request) {
      const response = await props.onGetAttendeeStatus(request, attendees, isNextDate);
      return response.data;
    }
    return [];
  };

  useEffect(() => {
    if (orgStore.outlookInfo && orgStore.orgInfo) {
      setAttendeeStatusRequest({
        startTime: {
          dateTime: orgStore.outlookInfo?.start,
          timeZone: timeZone,
        },
        endTime: {
          dateTime: orgStore.outlookInfo?.end,
          timeZone: timeZone,
        },
        schedules: attendees.map((item) => item.email),
        availabilityViewInterval: DefaultAvailabilityViewInterval,
      });
      setEventNextDate({});
      if(orgStore.outlookInfo?.start) {
        setTimeSlider(orgStore.outlookInfo?.start)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(orgStore.outlookInfo), JSON.stringify(attendees)]);

  useEffect(() => {
    setTimeSlider(orgStore.outlookInfo?.displayStartTime || new Date());
  }, [orgStore.outlookInfo?.displayStartTime]);

  const args: ICanvasProps = {
    headerTitle: (
      <HeaderTitleWrapper className="HeaderTitleWrapper">
        <AttendeeIcon theme={settingStore.theme} />

        <span>{`${t("Attendees")} ${
          attendeeStatusQuery.data?.length
            ? `(${attendeeStatusQuery.data.length})`
            : ""
        }`}</span>
      </HeaderTitleWrapper>
    ),
    isStickHeader: false,
    isOpen: props.isOpen,
    onOpen: (isOpen: boolean) => {
      if (props.onChangeOpenAttendeeList) {
        props.onChangeOpenAttendeeList(isOpen);
      }
    },
  };

  const contentT = (name: string, email: string) => {
    return (
      <AttendeeButtonsWrapper>
        <div className="content-tooltip">
          <div className="content-detail">
            <div className="item-detail">
              <div className="avatar avatar-name">
                <Avatar color="colorful" name={name} size={20} />
              </div>
              <div className="detail">{name}</div>
            </div>
            <div className="item-detail">
              <div className="avatar">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 13 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2 0C0.895431 0 0 0.895431 0 2V2.2013L6.0001 5.43212L12 2.20141V2C12 0.895431 11.1046 0 10 0H2ZM12 3.33716L6.23715 6.44024C6.08916 6.51992 5.91104 6.51992 5.76305 6.44024L0 3.33706V8C0 9.10457 0.895431 10 2 10H10C11.1046 10 12 9.10457 12 8V3.33716Z"
                    fill="#1678ce"
                  />
                </svg>
              </div>
              <div className="detail detail-email">{email ? email : "-"}</div>
            </div>
          </div>
        </div>
      </AttendeeButtonsWrapper>
    );
  };

  const styles = useStyles();

  const renderHeaderButtons = (attendee: AttendeeInfo): JSX.Element => {
    return (
      <>
        <div>
          <ScrollDismissTooltip content={t("Calendar")} relationship="label">
            <div
              onClick={(ev: React.MouseEvent<HTMLImageElement>) =>
                onOpenCalendar(ev, attendee)
              }
              {...BuildRCAttribute(`resource_item.${attendee.name}.calendar`)}
            >
              <RenderIconCalendar theme={settingStore.theme} />
            </div>
          </ScrollDismissTooltip>
        </div>
        <ScrollDismissTooltip
          withArrow
          content={{
            children: contentT(attendee.name, attendee.email),
            className: styles.tooltip,
          }}
          // content={contentT(attendee.name, attendee.email) }
          relationship="label"
          positioning={"above-end"}
        >
          <div {...BuildRCAttribute(`resource_item.${attendee.name}.detail`)}>
            <RenderIconInfo theme={settingStore.theme} />
          </div>
        </ScrollDismissTooltip>
        {!attendee.isOrganizer ? (
          <ScrollDismissTooltip content={t("Remove")} relationship="label">
            <div
              onClick={() => onRemoveAttendee(attendee)}
              {...BuildRCAttribute(`resource_item.${attendee.name}.remove`)}
            >
              <RenderIconDismiss theme={settingStore.theme} />
            </div>
          </ScrollDismissTooltip>
        ) : null}
      </>
    );
  };

  const onRemoveAttendee = (attendeeInfo: AttendeeInfo) => {
    if (props.onDeleteAttendee) {
      props.onDeleteAttendee(attendeeInfo);
    }
  };

  const changeTime = async (timeSliderClone: Date) => {
    setTimeSlider(timeSliderClone);
    if(timeSlider.getDate() !== timeSliderClone.getDate()) {
      const currentDateClone = new Date(
        orgStore.outlookInfo?.displayStartTime || ""
      );
      const beforeCurrenDate = new Date(currentDateClone);
      beforeCurrenDate.setDate(currentDateClone.getDate() - 1);
      const afterCurrenDate = new Date(currentDateClone);
      afterCurrenDate.setDate(currentDateClone.getDate() + 1);
      const startDateNext: string = TimeFunction.dateTimeFormatter(timeSliderClone, FORMAT_DATE_DEFAULT) as string
  
      if (
        attendees.length &&
        (!eventNextDate[attendees[0].email]?.startDate?.length ||
          !eventNextDate[attendees[0].email]?.startDate.includes(
            startDateNext
          )) &&
        (timeSliderClone <= beforeCurrenDate || timeSliderClone >= afterCurrenDate)
      ) {
        const request = {
          startTime: {
            dateTime: timeSliderClone,
            timeZone: timeZone,
          },
          endTime: {
            dateTime: timeSliderClone,
            timeZone: timeZone,
          },
          schedules: attendees.map((item) => item.email),
          availabilityViewInterval: DefaultAvailabilityViewInterval,
        };
        const resp = await onFetchData(request, true);
        const eventNextDateClone = cloneDeep(eventNextDate);
        resp.forEach(item => {
          if(!eventNextDateClone[item.email] || (eventNextDateClone[item.email] && !eventNextDateClone[item.email].startDate.includes(startDateNext))) {
            const startDateClone = eventNextDateClone[item.email]?.startDate || [];
            startDateClone.push(startDateNext)
            eventNextDateClone[item.email] = {
              startDate: startDateClone,
              event: (eventNextDateClone[item.email]?.event || []).concat(item.event || [])
            }
          }
        });
        setEventNextDate(eventNextDateClone);
      }
    }
  };

  // const onOpenCalendar = (
  //   ev: React.MouseEvent<HTMLImageElement>,
  //   attendeeInfo: AttendeeInfo
  // ) => {
  //   ev.stopPropagation();
  //   if (refCalendar?.current?.onOpenAttendeeCalendar) {
  //     refCalendar?.current?.onOpenAttendeeCalendar(attendeeInfo, orgStore.outlookInfo?.start || null);
  //   }
  // };

  const [selectedAttendeeInfo, setSelectedAttendeeInfo] =
    useState<AttendeeInfo | null>(null);
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);

  const onOpenCalendar = (
    ev: React.MouseEvent<HTMLImageElement>,
    attendeeInfo: AttendeeInfo
  ) => {
    setSelectedAttendeeInfo(attendeeInfo);
    setSelectedStartDate(orgStore.outlookInfo?.start || null);
  };

  return (
    <AttendeeBaseWrapper className="AttendeeBaseWrapper" $height={props.height}>
      <Canvas
        {...args}
        theme={settingStore.theme || undefined}
        rcName="resource_selected"
      >
        {attendeeStatusQuery.isLoading ? (
          <LoadingCustom />
        ) : (
          <>
            {attendeeStatusQuery.data &&
              attendeeStatusQuery.data?.map(
                (attendee: AttendeeInfo, index: number) => (
                  <AttendeeInfoWrapper
                    key={attendee?.guid || index}
                    className="AttendeeInfoWrapper"
                  >
                    <ResourceItem
                      isSelected={true}
                      id={attendee?.email}
                      avatar=""
                      name={`${attendee?.displayName} (${attendee?.email})`}
                      isShowClosingIcon={true}
                      leftBorderStyle={attendee?.calculateBorder}
                      isShowTimeLine={!orgStore.outlookInfo?.isRecurringMaster}
                      headerButtons={renderHeaderButtons(attendee)}
                      theme={settingStore.theme || undefined}
                      timeLineElements={
                        <TimeLine
                          timeSlider={timeSlider}
                          startTimeSelected={
                            orgStore.outlookInfo?.displayStartTime || new Date()
                          }
                          endTimeSelected={
                            orgStore.outlookInfo?.displayEndTime || new Date()
                          }
                          localizer="en-US"
                          isNoInformation={true}
                          dateTimeFormat={{
                            dateFormat: dateFormat || FORMAT_DATE_DEFAULT,
                            timeFormat: timeFormat || FORMAT_TIME_DEFAULT,
                          }}
                          isAttendee={true}
                          events={(attendee.event || []).concat(eventNextDate[attendee.email]?.event || [])}
                        />
                      }
                    />
                  </AttendeeInfoWrapper>
                )
              )}

            <StickyElementWrapper
              className="StickyElementWrapper"
              $theme={settingStore.theme || undefined}
            >
              <TimeRangeSliderCanvas
                key="resourceSelected"
                startTimeSelected={timeSlider}
                format={dateFormat || FORMAT_DATE_DEFAULT}
                formatHour={timeFormat || FORMAT_TIME_DEFAULT}
                onChange={changeTime}
                theme={settingStore.theme || undefined}
                rcName="resource_selected"
              />
            </StickyElementWrapper>
          </>
        )}
      </Canvas>
      <AttendeeCalendar
        attendeeInfo={selectedAttendeeInfo}
        startDate={selectedStartDate ?? new Date()}
        timeZone={timeZone}
        dateFormat={dateFormat}
        timeFormat={timeFormat}
        firstDayOfWeek={firstDayOfWeek}
        onClose={() => {
          setSelectedAttendeeInfo(null);
          setSelectedStartDate(null);
        }}
      />
    </AttendeeBaseWrapper>
  );
};

export default Attendee;
